import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const MensHealth = () => {
  const services = [
    {
      title: 'Erectile Dysfunction (ED)',
      description: 'Erectile dysfunction is common and treatable. We identify causes and find options that fit your goals.',
      path: '/services/erectile-dysfunction',
    },
    {
      title: "Peyronie's Disease",
      description: 'Curvature, pain, or functional changes can be evaluated. Earlier treatment often helps.',
      path: '/services/peyronies-disease',
    },
    {
      title: 'Low Testosterone (Low T)',
      description: 'Low energy, mood changes, and reduced libido may indicate low testosterone. Proper testing matters.',
      path: '/services/low-testosterone',
    },
    {
      title: 'Enlarged Prostate (BPH)',
      description: 'Frequent urination, weak stream, and nighttime waking can be addressed with evaluation and treatment.',
      path: '/services/bph',
    },
    {
      title: 'Vasectomy',
      description: 'Learn about the procedure, recovery timeline, and what to plan for before and after.',
      path: '/services/vasectomy',
    },
    {
      title: 'Urodynamics Testing',
      description: 'Advanced diagnostic testing to evaluate bladder function and urinary conditions.',
      path: '/services/urodynamics',
    },
    {
      title: 'GAINSWave Therapy',
      description: 'A breakthrough, non-invasive shockwave therapy for ED, Peyronie\'s disease, and enhanced sexual performance.',
      path: '/services/gainswave',
    },
  ];

  return (
    <div className="pb-20 md:pb-0" data-testid="mens-health-page">
      <SEO
        title="Men's Health Services"
        description="Comprehensive men's urology services at Havkin Urology: erectile dysfunction, low testosterone, BPH, Peyronie's disease, vasectomy, and urodynamics testing in Melbourne, FL."
        keywords="men's health services, urology services Melbourne FL, ED treatment, low testosterone therapy, BPH treatment, vasectomy, Peyronie's disease"
        canonical="/mens-health"
      />
      {/* Pope - Office Manager */}
      <section className="bg-white border-b border-slate-100" data-testid="pope-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-64 h-64 md:w-72 md:h-72 rounded-2xl overflow-hidden shadow-lg flex-shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_672d1315-e63e-45a4-b5f9-5a02f895fa2f/artifacts/4xx82aaf_IMG_3321.jpg"
                alt="Pope - Office Manager at Havkin Urology"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-2">Meet Pope</h2>
              <p className="text-black font-medium mb-3">Office Manager</p>
              <p className="text-slate-600 text-lg">Our beloved office manager, Pope, welcomes every patient who walks through our doors. He takes his job very seriously and ensures everyone feels right at home.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-6">
              Our Services
            </h1>
            <p className="text-lg md:text-xl text-slate-600">
              Sensitive concerns are common and treatable. We focus on clear evaluation and practical options.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.path}
                className="group bg-white p-8 rounded-xl border border-slate-200 hover:border-black/30 hover:shadow-lg transition-all"
                data-testid={`service-card-${index}`}
              >
                <h2 className="font-heading text-xl font-semibold text-primary mb-3 group-hover:text-black transition-colors">
                  {service.title}
                </h2>
                <p className="text-slate-600 mb-6">{service.description}</p>
                <span className="inline-flex items-center text-black font-medium text-sm">
                  Learn More
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Movember Team Photo */}
      <section className="bg-slate-50 py-12 md:py-16" data-testid="movember-team-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl overflow-hidden shadow-lg">
            <img
              src="/images/movember-team.png"
              alt="Havkin Urology Team - Supporting Movember & Men's Health"
              className="w-full h-auto"
            />
          </div>
          <p className="text-center text-slate-500 mt-4 text-sm font-medium">Our team proudly supporting Movember & men's health awareness</p>
        </div>
      </section>

      {/* Insurance Accepted */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="services-insurance-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
            Insurances Accepted
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {['Blue Cross Blue Shield', 'UMR', 'GEHA', 'United Healthcare', 'Anthem'].map((ins) => (
              <div key={ins} className="bg-white rounded-lg p-4 text-center border border-slate-200">
                <p className="font-medium text-primary text-sm">{ins}</p>
              </div>
            ))}
          </div>
          <p className="text-slate-600 text-center">
            Please contact our office to verify your coverage before your appointment.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Have Questions?
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Schedule a consultation to discuss your concerns in a private, professional setting.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-black rounded-full font-medium hover:bg-gray-100 transition-colors"
              data-testid="mens-health-cta"
            >
              <Calendar className="h-5 w-5 mr-2" />
              Request Appointment
            </Link>
            <a
              href="tel:+13215004545"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white rounded-full font-medium hover:bg-white/10 transition-colors"
            >
              <Phone className="h-5 w-5 mr-2" />
              (321) 500-4545
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MensHealth;
